/**
 * ThemeManager - Resolves developer theme config into concrete colors
 * Implements WCAG 2.1 AA auto-contrast (4.5:1 ratio)
 */

import type { ThemeConfig, ThemeMode } from '../types/config';

export interface ResolvedTheme {
  primaryColor: string;
  secondaryColor: string;
  textColor: string;
  textOnPrimary: string;
  backgroundColor: string;
  borderColor: string;
  borderRadius: number;
  mode: 'light' | 'dark';
}

const LIGHT_DEFAULTS = {
  primaryColor: '#1976d2',
  secondaryColor: '#f5f5f5',
  backgroundColor: '#ffffff',
  borderColor: '#e0e0e0',
};

const DARK_DEFAULTS = {
  primaryColor: '#42a5f5',
  secondaryColor: '#2c2c2c',
  backgroundColor: '#1e1e1e',
  borderColor: '#444444',
};

/**
 * Parse hex color to RGB components
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const cleaned = hex.replace('#', '');
  const full = cleaned.length === 3
    ? cleaned.split('').map(c => c + c).join('')
    : cleaned;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/**
 * Relative luminance per WCAG 2.1
 */
function relativeLuminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  const [rs, gs, bs] = [r, g, b].map(c => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Contrast ratio between two colors (WCAG 2.1)
 */
export function contrastRatio(hex1: string, hex2: string): number {
  const l1 = relativeLuminance(hex1);
  const l2 = relativeLuminance(hex2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Get black or white text for WCAG AA (4.5:1) contrast against a background
 */
export function getContrastColor(bgColor: string): string {
  const whiteRatio = contrastRatio(bgColor, '#ffffff');
  const blackRatio = contrastRatio(bgColor, '#000000');
  return blackRatio >= whiteRatio ? '#000000' : '#ffffff';
}

/**
 * Detect user's preferred color scheme
 */
function detectColorScheme(): 'light' | 'dark' {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

/**
 * Resolve a theme config (or simple mode string) into concrete color values
 */
export function resolveTheme(config?: ThemeMode | ThemeConfig): ResolvedTheme {
  // Handle simple string mode
  const themeConfig: ThemeConfig = typeof config === 'string'
    ? { mode: config }
    : (config || {});

  // Resolve mode
  const rawMode = themeConfig.mode || 'auto';
  const mode: 'light' | 'dark' = rawMode === 'auto' ? detectColorScheme() : rawMode;

  const defaults = mode === 'dark' ? DARK_DEFAULTS : LIGHT_DEFAULTS;

  const primaryColor = themeConfig.primaryColor || defaults.primaryColor;
  const secondaryColor = themeConfig.secondaryColor || defaults.secondaryColor;
  const backgroundColor = themeConfig.backgroundColor || defaults.backgroundColor;
  const borderColor = defaults.borderColor;
  const borderRadius = themeConfig.borderRadius ?? 12;

  // Auto-calculate text colors for WCAG AA compliance
  const textColor = themeConfig.textColor || getContrastColor(backgroundColor);
  const textOnPrimary = getContrastColor(primaryColor);

  return {
    primaryColor,
    secondaryColor,
    textColor,
    textOnPrimary,
    backgroundColor,
    borderColor,
    borderRadius,
    mode,
  };
}
